/**
 * Service d'authentification Native Mobile SSO v1.0
 * Architecture Frontend-First : Direct IAM après encryption
 * 
 * @version 2.7.9
 */

import { 
  ApiError, 
  fetchWithTimeout,
  getHeaders,
  getDeviceId,
  getNativeStorage,
  ensureDeviceContext,
  setAuthToken,
  setAuthUser,
  clearAuthToken,
  getNativeAuthConfig,
  setNativeAuthConfig,
  isDebugMode,
  STORAGE_KEYS,
  getAuthToken,
} from './api';

import type {
  NativeAuthType,
  NativeEncryptRequest,
  NativeEncryptResponse,
  NativeInitResponse,
  NativeValidateResponse,
  NativeGrantAccessResponse,
  NativeResendOtpResponse,
  NativeExchangeResponse,
  NativeConfigResponse,
  NativeCredentials,
  NativeRefreshResponse,
} from '../types/native';

// ============================================
// Credentials en mémoire (jamais persistés)
// ============================================

let credentials: NativeCredentials | null = null;
let credentialsLoadedAt: number = 0;
let credentialsTtl: number = 300; // Default 5 minutes
let refreshInFlight: Promise<NativeRefreshResponse> | null = null;
let lastSuccessfulRefreshAt = 0;
let lastSuccessfulRefreshResponse: NativeRefreshResponse | null = null;

const DEFAULT_TTL = 300;
const REFRESH_MARGIN = 30; // Refresh 30s before expiration
const REFRESH_COOLDOWN_MS = 60 * 1000;

// ============================================
// Service Native Auth
// ============================================

export const nativeAuthService = {
  hasCredentials(): boolean {
    return credentials !== null;
  },

  getCredentials(): NativeCredentials | null {
    return credentials ? { ...credentials } : null;
  },

  getCredentialsTtl(): number {
    return credentialsTtl;
  },

  getCredentialsAge(): number {
    if (!credentialsLoadedAt) return Infinity;
    return (Date.now() - credentialsLoadedAt) / 1000;
  },

  areCredentialsExpiringSoon(): boolean {
    if (!credentials || !credentialsLoadedAt) return true;
    const age = this.getCredentialsAge();
    return age >= (credentialsTtl - REFRESH_MARGIN);
  },

  async ensureFreshCredentials(): Promise<void> {
    if (!credentials || this.areCredentialsExpiringSoon()) {
      if (isDebugMode()) {
        console.log('🔄 [Native] Credentials expirés ou proches — auto-refresh');
      }
      await this.loadCredentials();
    }
  },

  async loadCredentials(): Promise<NativeCredentials> {
    const config = getNativeAuthConfig();
    if (!config.saasApiUrl) {
      throw new ApiError('saasApiUrl non configurée', 'unknown');
    }

    await ensureDeviceContext();

    if (isDebugMode()) {
      console.log('📤 [SaaS] GET /sso/config');
    }

    const response = await fetchWithTimeout<NativeConfigResponse>(
      `${config.saasApiUrl}/sso/config`,
      { method: 'GET', headers: getHeaders(undefined, true) },
      config.timeout || 30000
    );

    if (!response.success || !response.encrypted_credentials || !response.app_key) {
      throw new ApiError('Configuration invalide reçue du SaaS (app_key ou encrypted_credentials manquant)', 'server');
    }

    credentials = {
      appKey: response.app_key,
      encryptedCredentials: response.encrypted_credentials,
    };

    credentialsLoadedAt = Date.now();
    credentialsTtl = response.credentials_ttl || DEFAULT_TTL;

    // Propager le debug mode depuis le backend (si défini)
    const currentConfig = getNativeAuthConfig();
    if (typeof response.debug === 'boolean') {
      if (currentConfig.debug !== true) {
        setNativeAuthConfig({ debug: response.debug });
      }
    }

    if (typeof response.bypass === 'boolean') {
      setNativeAuthConfig({ bypass: response.bypass });
    }

    if (isDebugMode()) {
      console.log('✅ [SaaS] Credentials chargés (ttl:', credentialsTtl, 's, debug:', response.debug ?? 'non défini', ')');
    }

    return credentials;
  },

  async encrypt(
    type: NativeAuthType,
    data: Partial<NativeEncryptRequest>
  ): Promise<NativeEncryptResponse> {
    // Auto-refresh credentials if expiring soon
    await this.ensureFreshCredentials();

    if (!credentials) {
      throw new ApiError('Credentials non chargés. Appelez loadCredentials() d\'abord.', 'auth');
    }

    const config = getNativeAuthConfig();
    if (!config.iamApiUrl) {
      throw new ApiError('iamApiUrl non configurée', 'unknown');
    }

    await ensureDeviceContext();

    const payload: NativeEncryptRequest = {
      type,
      app_key: credentials.appKey,
      encrypted_credentials: credentials.encryptedCredentials,
      ...data,
    };

    if (isDebugMode()) {
      const safeData = {
        ...data,
      } as Partial<NativeEncryptRequest> & {
        password?: string;
        otp_code?: string;
        email?: string;
        phone?: string;
        ccphone?: string;
      };
      if (typeof safeData.password === 'string') safeData.password = '[redacted]';
      if (typeof safeData.otp_code === 'string') safeData.otp_code = '[redacted]';
      if (typeof safeData.email === 'string') safeData.email = safeData.email.includes('@')
        ? safeData.email.replace(/(^.).*(@.*$)/, '$1***$2')
        : '[redacted]';
      if (typeof safeData.ccphone === 'string') {
        safeData.ccphone = '[redacted]';
      }
      if (typeof safeData.phone === 'string') {
        safeData.phone = '[redacted]';
      }
      console.log('📤 [IAM] POST /sso/native/encrypt', { type, ...safeData });
    }

    const response = await fetchWithTimeout<NativeEncryptResponse>(
      `${config.iamApiUrl}/sso/native/encrypt`,
      {
        method: 'POST',
        headers: getHeaders(undefined, false),
        body: JSON.stringify(payload),
      },
      config.timeout || 30000
    );

    if (isDebugMode()) {
      console.log('✅ [IAM] native_token reçu, expire dans', response.expires_in, 's');
    }

    return response;
  },

  async init(nativeToken: string): Promise<NativeInitResponse> {
    const config = getNativeAuthConfig();
    if (!config.iamApiUrl) {
      throw new ApiError('iamApiUrl non configurée', 'unknown');
    }

    await ensureDeviceContext();

    if (isDebugMode()) {
      console.log('📤 [IAM] POST /sso/native/init');
    }

    const response = await fetchWithTimeout<NativeInitResponse>(
      `${config.iamApiUrl}/sso/native/init`,
      {
        method: 'POST',
        headers: getHeaders(undefined, false),
        body: JSON.stringify({ native_token: nativeToken }),
      },
      config.timeout || 30000
    );

    if (isDebugMode()) {
      console.log('✅ [IAM] Init:', { status: response.status, hasProcessToken: !!response.process_token });
    }

    return response;
  },

  async validate(
    processToken: string,
    creds: { password?: string; otp_code?: string }
  ): Promise<NativeValidateResponse> {
    const config = getNativeAuthConfig();
    if (!config.iamApiUrl) {
      throw new ApiError('iamApiUrl non configurée', 'unknown');
    }

    await ensureDeviceContext();

    if (isDebugMode()) {
      console.log('📤 [IAM] POST /sso/native/validate');
    }

    const response = await fetchWithTimeout<NativeValidateResponse>(
      `${config.iamApiUrl}/sso/native/validate`,
      {
        method: 'POST',
        headers: getHeaders(undefined, false),
        body: JSON.stringify({
          process_token: processToken,
          ...creds,
        }),
      },
      config.timeout || 30000
    );

    if (isDebugMode()) {
      console.log('✅ [IAM] Validate:', { 
        hasCallback: !!response.callback_token, 
        requires2fa: response.requires_2fa 
      });
    }

    return response;
  },

  async verifyTotp(
    processToken: string,
    totpCode: string
  ): Promise<NativeValidateResponse> {
    const config = getNativeAuthConfig();
    if (!config.iamApiUrl) {
      throw new ApiError('iamApiUrl non configurée', 'unknown');
    }

    await ensureDeviceContext();

    if (isDebugMode()) {
      console.log('📤 [IAM] POST /sso/native/verify-totp');
    }

    return fetchWithTimeout<NativeValidateResponse>(
      `${config.iamApiUrl}/sso/native/verify-totp`,
      {
        method: 'POST',
        headers: getHeaders(undefined, false),
        body: JSON.stringify({
          process_token: processToken,
          totp_code: totpCode,
        }),
      },
      config.timeout || 30000
    );
  },

  async grantAccess(processToken: string): Promise<NativeGrantAccessResponse> {
    const config = getNativeAuthConfig();
    if (!config.iamApiUrl) {
      throw new ApiError('iamApiUrl non configurée', 'unknown');
    }

    await ensureDeviceContext();

    if (isDebugMode()) {
      console.log('📤 [IAM] POST /sso/native/grant-access');
    }

    return fetchWithTimeout<NativeGrantAccessResponse>(
      `${config.iamApiUrl}/sso/native/grant-access`,
      {
        method: 'POST',
        headers: getHeaders(undefined, false),
        body: JSON.stringify({ process_token: processToken }),
      },
      config.timeout || 30000
    );
  },

  async resendOtp(processToken: string): Promise<NativeResendOtpResponse> {
    const config = getNativeAuthConfig();
    if (!config.iamApiUrl) {
      throw new ApiError('iamApiUrl non configurée', 'unknown');
    }

    await ensureDeviceContext();

    if (isDebugMode()) {
      console.log('📤 [IAM] POST /sso/native/resend-otp');
    }

    return fetchWithTimeout<NativeResendOtpResponse>(
      `${config.iamApiUrl}/sso/native/resend-otp`,
      {
        method: 'POST',
        headers: getHeaders(undefined, false),
        body: JSON.stringify({ process_token: processToken }),
      },
      config.timeout || 30000
    );
  },

  async exchange(callbackToken: string): Promise<NativeExchangeResponse> {
    const config = getNativeAuthConfig();
    if (!config.saasApiUrl) {
      throw new ApiError('saasApiUrl non configurée', 'unknown');
    }

    await ensureDeviceContext();

    if (isDebugMode()) {
      console.log('📤 [SaaS] POST /sso/exchange');
    }

    const response = await fetchWithTimeout<NativeExchangeResponse>(
      `${config.saasApiUrl}/sso/exchange`,
      {
        method: 'POST',
        headers: getHeaders(undefined, true),
        body: JSON.stringify({ callback_token: callbackToken }),
      },
      config.timeout || 30000
    );

    if (response.iam_token && response.reference && response.alias_reference && response.user_infos) {
      setAuthToken(response.iam_token);
      setAuthUser({
        reference: response.reference,
        alias_reference: response.alias_reference,
        ...response.user_infos,
      });
    // Persister les métadonnées de session si elles sont fournies
      const storage = getNativeStorage();
      if (response.expires_at) {
        storage.setItem(STORAGE_KEYS.TOKEN_EXPIRES_AT, response.expires_at);
      }
      if (response.refresh_expires_at) {
        storage.setItem(STORAGE_KEYS.REFRESH_EXPIRES_AT, response.refresh_expires_at);
      }
      // Persister alias_reference pour accès rapide
      storage.setItem(STORAGE_KEYS.ALIAS_REFERENCE, response.alias_reference);
    }

    if (isDebugMode()) {
      console.log('✅ [SaaS] Session établie:', { reference: response.reference });
    }

    return response;
  },

  async checkToken(token: string): Promise<{ valid: boolean; user?: import('../types/native').UserInfos }> {
    const cfg = getNativeAuthConfig();
    if (!cfg.saasApiUrl) {
      throw new ApiError('saasApiUrl non configurée', 'unknown');
    }

    await ensureDeviceContext();

    if (isDebugMode()) {
      console.log('📤 [SaaS] POST /sso/check-token');
    }

    try {
      const response = await fetchWithTimeout<{
        status?: string;
        reference?: string;
        alias_reference?: string;
        iam_token?: string;
        user_infos?: import('../types/native').UserInfos;
        success?: boolean;
      }>(
        `${cfg.saasApiUrl}/sso/check-token`,
        {
          method: 'POST',
          headers: getHeaders(token, true),
        },
        10000
      );

      if (response.reference && response.alias_reference && response.iam_token && response.user_infos) {
        return {
          valid: true,
          user: {
            reference: response.reference,
            name: response.user_infos?.name || '',
            email: response.user_infos?.email,
            ccphone: response.user_infos?.ccphone,
            phone: response.user_infos?.phone,
            address: response.user_infos?.address,
            town: response.user_infos?.town,
            country: response.user_infos?.country,
            image_url: response.user_infos?.image_url,
            auth_2fa: response.user_infos?.auth_2fa,
            alias_reference: response.alias_reference,
          },
        };
      }

      return { valid: false };
    } catch (err) {
      if (err instanceof ApiError && err.statusCode === 401) {
        return { valid: false };
      }
      throw err;
    }
  },

  async refresh(): Promise<NativeRefreshResponse> {
    const now = Date.now();

    if (refreshInFlight) {
      return refreshInFlight;
    }

    if (
      lastSuccessfulRefreshResponse?.iam_token
      && lastSuccessfulRefreshAt
      && now - lastSuccessfulRefreshAt < REFRESH_COOLDOWN_MS
    ) {
      if (isDebugMode()) {
        console.log('🔄 [SaaS] POST /sso/refresh — cooldown actif, réponse réutilisée');
      }
      return lastSuccessfulRefreshResponse;
    }

    const cfg = getNativeAuthConfig();
    if (!cfg.saasApiUrl) {
      throw new ApiError('saasApiUrl non configurée', 'unknown');
    }

    await ensureDeviceContext();

    const refreshToken = getNativeStorage().getItem(STORAGE_KEYS.REFRESH_TOKEN);

    if (!refreshToken) {
      return { success: false, error_type: 'missing_refresh', message: 'Refresh token manquant' };
    }

    if (isDebugMode()) {
      console.log('📤 [SaaS] POST /sso/refresh');
    }

    refreshInFlight = (async () => {
      try {
        let response: NativeRefreshResponse;
        try {
          response = await fetchWithTimeout<NativeRefreshResponse>(
            `${cfg.saasApiUrl}/sso/refresh`,
            {
              method: 'POST',
              headers: getHeaders(undefined, true),
              body: JSON.stringify({ refresh_token: refreshToken }),
            },
            cfg.timeout || 30000
          );
        } catch (err) {
          // Normalize common cases for callers (do not force logout on network errors).
          if (err instanceof ApiError) {
            if (err.statusCode === 401) {
              response = {
                success: false,
                error_type: err.errorType || 'invalid_refresh',
                message: err.message,
              };
            } else if (err.statusCode === 404) {
              response = {
                success: false,
                error_type: 'not_supported',
                message: 'Endpoint refresh non disponible sur ce SaaS',
              };
            } else {
              throw err;
            }
          } else {
            throw err;
          }
        }

        // Backends can return {success:false,...} with 200 (not recommended),
        // but we keep the contract tolerant and only update state on explicit success.
        // Refresh contract:
        // - conserver iam_token quand le SaaS le renvoie
        // - mettre à jour expires_at / refresh_expires_at si le SaaS fait tourner les métadonnées
        if (response.iam_token && response.reference && response.alias_reference && response.user_infos) {
          setAuthToken(response.iam_token);

          const storage = getNativeStorage();
          if (response.expires_at) storage.setItem(STORAGE_KEYS.TOKEN_EXPIRES_AT, response.expires_at);
          if (response.refresh_expires_at) storage.setItem(STORAGE_KEYS.REFRESH_EXPIRES_AT, response.refresh_expires_at);
          storage.setItem(STORAGE_KEYS.ALIAS_REFERENCE, response.alias_reference);
          setAuthUser({
            reference: response.reference,
            alias_reference: response.alias_reference,
            ...response.user_infos,
          });

          lastSuccessfulRefreshAt = Date.now();
          lastSuccessfulRefreshResponse = response;
        }

        return response;
      } finally {
        refreshInFlight = null;
      }
    })();

    return refreshInFlight;
  },

  async logout(token?: string): Promise<{ success: boolean }> {
    const config = getNativeAuthConfig();
    await ensureDeviceContext();

    // Récupérer les identifiants avant nettoyage
    const storage = getNativeStorage();
    const iamToken = getAuthToken();

    // Double revocation : SaaS + IAM en parallèle (best-effort)
    const promises: Promise<unknown>[] = [];

    // ① Appel SaaS — révoque le Sanctum token
    if (config.saasApiUrl && token) {
      promises.push(
        fetchWithTimeout<{ success: boolean }>(
          `${config.saasApiUrl}/sso/logout`,
          {
            method: 'POST',
            headers: getHeaders(token, true),
          },
          config.timeout || 30000
        ).catch((err) => {
          if (isDebugMode()) {
            console.warn('⚠️ [SaaS] Échec logout (non-bloquant):', err instanceof Error ? err.message : err);
          }
        })
      );
    }

    // ② Appel direct IAM — révoque le token de session via sa référence stable
    if (config.iamApiUrl && iamToken) {
      const payload: Record<string, string> = { iam_token: iamToken };

      promises.push(
        fetchWithTimeout<{ success: boolean }>(
          `${config.iamApiUrl}/sso/disconnect`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify(payload),
          },
          5000
        ).catch((err) => {
          if (isDebugMode()) {
            console.warn('⚠️ [IAM] Échec disconnect (non-bloquant):', err instanceof Error ? err.message : err);
          }
        })
      );
    }

    // Attendre les deux sans bloquer
    await Promise.allSettled(promises);

    if (isDebugMode()) {
      console.log('✅ [Logout] Double revocation terminée — nettoyage local');
    }

    // Nettoyage local garanti (même si les deux appels échouent)
    clearAuthToken();
    credentials = null;
    credentialsLoadedAt = 0;
    refreshInFlight = null;
    lastSuccessfulRefreshAt = 0;
    lastSuccessfulRefreshResponse = null;
    return { success: true };
  },

  clearCredentials(): void {
    credentials = null;
    credentialsLoadedAt = 0;
    refreshInFlight = null;
    lastSuccessfulRefreshAt = 0;
    lastSuccessfulRefreshResponse = null;
  },

  // ============================================
  // High-level methods
  // ============================================

  async loginEmail(
    email: string
  ): Promise<NativeInitResponse> {
    const { native_token } = await this.encrypt('login_email', {
      email,
    });
    return this.init(native_token);
  },

  async loginPhone(
    ccphone: string,
    phone: string
  ): Promise<NativeInitResponse> {
    const { native_token } = await this.encrypt('login_phone', {
      ccphone,
      phone,
    });
    return this.init(native_token);
  },

  async loginAccessOtp(otpCode: string): Promise<NativeInitResponse> {
    const { native_token } = await this.encrypt('login_access_otp', {
      otp_code: otpCode.replace(/[\s-]/g, ''),
    });
    return this.init(native_token);
  },

  async register(data: {
    name: string;
    email?: string;
    ccphone: string;
    phone: string;
    town?: string;
    country?: string;
    registration_type?: 'email' | 'phone';
  }): Promise<NativeInitResponse> {
    const { native_token } = await this.encrypt('register', {
      ...data,
      registration_type: data.registration_type || 'email',
    });
    return this.init(native_token);
  },

  async registerPhoneOnly(data: {
    name: string;
    ccphone: string;
    phone: string;
    town?: string;
    country?: string;
  }): Promise<NativeInitResponse> {
    if (data.ccphone !== '+221') {
      throw new ApiError(
        "L'inscription par téléphone est réservée aux numéros sénégalais (+221)",
        'validation',
        422,
        'invalid_country_code'
      );
    }

    const { native_token } = await this.encrypt('register', {
      ...data,
      email: '',
      registration_type: 'phone',
    });
    return this.init(native_token);
  },

  async recoverPassword(
    method: 'email' | 'phone',
    identifier: { email?: string; ccphone?: string; phone?: string }
  ): Promise<NativeInitResponse> {
    const { native_token } = await this.encrypt('recovery_password', {
      method,
      ...identifier,
    });
    return this.init(native_token);
  },
};

export default nativeAuthService;
